import { useState, useEffect, memo, createContext, useContext, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Colors } from 'styles';
import type { RightDrawerPreviewItemType, RightDrawerTabType } from './types';
import { usePicklistEditor } from '../../Template/usePicklist';
import { useActivePicklistState } from '../../Template/useActivePicklistState';
import usePrevious from 'react-use/esm/usePrevious';
import { useLocation } from 'react-router-dom';
import { RightDrawerTabSection as RightDrawerTabSectionInternal } from './RightDrawerTabSection';
import { RightDrawerPreviewSection as RightDrawerPreviewSectionInternal } from './RightDrawerPreviewSection';
import { RightDrawerPicklistSection as RightDrawerPicklistSectionInternal } from './RightDrawerPicklistSection';
import { RightDrawerMacroSection as RightDrawerMacroSectionInternal } from './RightDrawerMacroSection';
import type { LazyQueryHookExecOptions } from '@apollo/client';
import { useRecoilState } from 'recoil';
import { rightDrawerFilterState } from './state';
import { GetMacroQuery, GetMacroQueryVariables } from 'generated/graphql';
import { Stack } from 'common/ui/Layout';
import { resolveRightDrawerBasePath } from './paths';
import type { LoadTemplate } from './RightDrawerTabSection';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${Colors.gray3};
`;

type RightDrawerCompositionContextType = {
  activeTab: RightDrawerTabType;
  setActiveTab: (tab: RightDrawerTabType) => void;
  basePath: string;
  previewItem: Nullable<RightDrawerPreviewItemType>;
  setPreviewItem: (item?: Nullable<RightDrawerPreviewItemType>) => void;
  forceShowPreviewItem: boolean;
  setForceShowPreviewItem: (show: boolean) => void;
  isPreviewLoading: boolean;
  setIsPreviewLoading: (isLoading: boolean) => void;
  activePicklist: unknown;
};

type RightDrawerRootProps = {
  children: React.ReactNode;
  className?: string;
  defaultTab?: RightDrawerTabType;
  basePath?: string;
};

export type RightDrawerTabSectionProps = {
  drawerHeight: number;
  isSubmittedStatus: boolean;
  procedureSmid?: string;
  onLoadTemplate?: LoadTemplate;
  onLoadMacro?: (
    options: Partial<LazyQueryHookExecOptions<GetMacroQuery, GetMacroQueryVariables>>
  ) => Promise<void>;
  disabled?: boolean;
  disabledDoubleClick: boolean;
};

export type RightDrawerPreviewSectionProps = Record<never, never>;

export type RightDrawerPreviewOrPicklistSectionProps = Record<never, never>;
export type RightDrawerMacroSectionProps = Record<never, never>;

export type RightDrawerFieldBuilderSectionProps = {
  children?: React.ReactNode;
  minHeight?: number;
  flexBasis?: string;
  showDivider?: boolean;
};
export const RIGHT_DRAWER_FIELD_BUILDER_MIN_HEIGHT = 240;
export const RIGHT_DRAWER_FIELD_BUILDER_FLEX_BASIS = '45%';

const RightDrawerCompositionContext =
  createContext<Nullable<RightDrawerCompositionContextType>>(undefined);

const useRightDrawerCompositionContext = (): RightDrawerCompositionContextType => {
  const context = useContext(RightDrawerCompositionContext);

  if (context == null) {
    throw new Error('RightDrawer compound sections must be rendered inside <RightDrawer.Root>.');
  }

  return context;
};

const RightDrawerRoot = memo(
  ({ children, className, defaultTab, basePath }: RightDrawerRootProps): React.ReactElement => {
    const location = useLocation();
    const [filterState, setFilterState] = useRecoilState(rightDrawerFilterState);
    const [previewItemState, setPreviewItemState] =
      useState<Nullable<RightDrawerPreviewItemType>>(null);
    const [forceShowPreviewItem, setForceShowPreviewItem] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const { activePicklist } = usePicklistEditor();
    const prevActivePicklist = usePrevious(activePicklist);

    // Use persisted active tab, but allow defaultTab prop to override on initial load
    const [activeTab, setActiveTab] = useState<RightDrawerTabType>(
      defaultTab ?? filterState.activeTab
    );
    const resolvedBasePath = resolveRightDrawerBasePath({
      basePath,
      pathname: location.pathname,
    });

    const setPreviewItem = useCallback((item?: Nullable<RightDrawerPreviewItemType>) => {
      setPreviewItemState(item ?? null);
    }, []);

    useEffect(() => {
      setFilterState((prev) => ({
        ...prev,
        activeTab,
      }));
    }, [activeTab, setFilterState]);

    useEffect(() => {
      if (
        (activePicklist != null && prevActivePicklist == null) ||
        activePicklist !== prevActivePicklist
      ) {
        setForceShowPreviewItem(false);
      }
    }, [activePicklist, prevActivePicklist, setForceShowPreviewItem]);

    const contextValue = useMemo(
      () => ({
        activeTab,
        setActiveTab,
        basePath: resolvedBasePath,
        previewItem: previewItemState,
        setPreviewItem,
        forceShowPreviewItem,
        setForceShowPreviewItem,
        isPreviewLoading,
        setIsPreviewLoading,
        activePicklist,
      }),
      [
        activePicklist,
        activeTab,
        resolvedBasePath,
        forceShowPreviewItem,
        isPreviewLoading,
        previewItemState,
        setPreviewItem,
      ]
    );

    return (
      <RightDrawerCompositionContext.Provider value={contextValue}>
        <Container className={className}>{children}</Container>
      </RightDrawerCompositionContext.Provider>
    );
  }
);

RightDrawerRoot.displayName = 'RightDrawer.Root';

const RightDrawerTabSection = memo(
  ({
    drawerHeight,
    isSubmittedStatus,
    procedureSmid = '',
    onLoadTemplate,
    onLoadMacro,
    disabled = false,
    disabledDoubleClick,
  }: RightDrawerTabSectionProps): React.ReactElement => {
    const {
      previewItem,
      setPreviewItem,
      setForceShowPreviewItem,
      activeTab,
      basePath,
      setActiveTab,
      setIsPreviewLoading,
    } = useRightDrawerCompositionContext();

    return (
      <RightDrawerTabSectionInternal
        drawerHeight={drawerHeight}
        isSubmittedStatus={isSubmittedStatus}
        onLoadTemplate={onLoadTemplate}
        onLoadMacro={onLoadMacro}
        procedureSmid={procedureSmid}
        basePath={basePath}
        disabledDoubleClick={disabledDoubleClick}
        disabled={disabled}
        previewItem={previewItem}
        setPreviewItem={setPreviewItem}
        setForceShowPreviewItem={setForceShowPreviewItem}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setIsPreviewLoading={setIsPreviewLoading}
      />
    );
  }
);

RightDrawerTabSection.displayName = 'RightDrawer.TabSection';

const RightDrawerPreviewSection = memo((_: RightDrawerPreviewSectionProps): React.ReactElement => {
  const { previewItem, activeTab, isPreviewLoading, basePath } = useRightDrawerCompositionContext();

  return (
    <RightDrawerPreviewSectionInternal
      previewItem={previewItem}
      activeTab={activeTab}
      isPreviewLoading={isPreviewLoading}
      basePath={basePath}
    />
  );
});

RightDrawerPreviewSection.displayName = 'RightDrawer.PreviewSection';

const RightDrawerPicklistSection = memo((): React.ReactElement => {
  return <RightDrawerPicklistSectionInternal />;
});

RightDrawerPicklistSection.displayName = 'RightDrawer.PicklistSection';

const RightDrawerPreviewOrPicklistSection = memo(
  (_: RightDrawerPreviewOrPicklistSectionProps): React.ReactElement => {
    const { forceShowPreviewItem } = useRightDrawerCompositionContext();
    const { hasActivePicklist } = useActivePicklistState();

    if (!forceShowPreviewItem && hasActivePicklist) {
      return <RightDrawerPicklistSection />;
    }

    return <RightDrawerPreviewSection />;
  }
);

RightDrawerPreviewOrPicklistSection.displayName = 'RightDrawer.PreviewOrPicklistSection';

const RightDrawerMacroSection = memo((_: RightDrawerMacroSectionProps): React.ReactElement => {
  return <RightDrawerMacroSectionInternal />;
});

RightDrawerMacroSection.displayName = 'RightDrawer.MacroSection';

const RightDrawerFieldBuilderSection = memo(
  ({
    children,
    minHeight = RIGHT_DRAWER_FIELD_BUILDER_MIN_HEIGHT,
    flexBasis = RIGHT_DRAWER_FIELD_BUILDER_FLEX_BASIS,
    showDivider = true,
  }: RightDrawerFieldBuilderSectionProps): React.ReactElement => {
    return (
      <Stack
        vertical
        css={`
          min-height: ${minHeight}px;
          flex: 0 0 ${flexBasis};
          overflow: auto;
          ${showDivider ? `border-bottom: 1px solid ${Colors.gray4};` : ''}
        `}
        space="none"
      >
        <>{children}</>
      </Stack>
    );
  }
);

RightDrawerFieldBuilderSection.displayName = 'RightDrawer.FieldBuilderSection';
export const RightDrawer = Object.freeze({
  Root: RightDrawerRoot,
  TabSection: RightDrawerTabSection,
  MacroSection: RightDrawerMacroSection,
  PreviewSection: RightDrawerPreviewSection,
  PicklistSection: RightDrawerPicklistSection,
  PreviewOrPicklistSection: RightDrawerPreviewOrPicklistSection,
  FieldBuilderSection: RightDrawerFieldBuilderSection,
});
